// React
import { ReactNode, useCallback } from 'react'

// Interfaces
import { ISharedQuestionSetTableProps } from './interfaces'

// Components
import { BaseTableBody } from 'components'

// Antd
import { ColumnsType } from 'antd/es/table'

// Interfaces
import {
  ISharedAnswer,
  ISharedQuestionSetHeader,
  ISharedQuestionSetResult,
  ISharedQuestionSetResultDetail,
} from 'interfaces/shared'
import { ISharedCommonFileUploadAttrs, ISharedCommonUpdateAnswerAttrs } from 'interfaces/shared/shared-common.interface'

// Custom Hooks
import { useQuestion } from 'hooks'

// Constant
import { QUESTION_TYPES, COLUMN_TYPE, ASSESSMENT_FEATURES } from 'constant/shared.constant'

// Mobx
import { observer } from 'mobx-react'

// Lodash
import isEmpty from 'lodash/isEmpty'
import flattenDeep from 'lodash/flattenDeep'

// Question Types
const { FILE_UPLOAD, MULTIPLE_CHOICE, CHECKBOX, SLIDER, PARAGRAPH } = QUESTION_TYPES

const SharedQuestionSetTable = observer(
  ({
    data,
    onUpdateUserAnswer,
    onUploadDocument,
    onDeleteDocument,
    onDownloadDocument,
    onAddFileItem,
    onDeleteFileItem,
    isReadOnly,
    featureName,
    questionSetId
  }: ISharedQuestionSetTableProps) => {
    // Hook
    const { question_renderQuestion, question_renderAnswer } = useQuestion({ isReadOnly })

    /**
     * @description Handle column render
     *
     * @param {ISharedQuestionSetResult} result
     * @param {number} headerId
     * @param {boolean} isChildren
     *
     * @return {ReactNode} ReactNode
     */
    const render = useCallback(
      (result: ISharedQuestionSetResult, headerId: number, isChildren?: boolean): ReactNode => {
        const resultDetailData = result.resultDetail.find(resultDetail => {
          if (isChildren) {
            return resultDetail.childrenHeaderId === headerId
          } else {
            return resultDetail.headerId === headerId
          }
        }) as ISharedQuestionSetResultDetail

        // Check if resultDetailData exists
        if (resultDetailData) {
          if (resultDetailData.type === COLUMN_TYPE.QUESTION) {
            return question_renderQuestion(resultDetailData)
          } else {
            return question_renderAnswer(
              resultDetailData,
              (identifiers: ISharedCommonUpdateAnswerAttrs, _data: any, attrs?: ISharedCommonFileUploadAttrs) => {
                const _answerCode = resultDetailData.answer?.answerCode!
                const commonIdentifiers = {
                  questionSetId: questionSetId!,
                  resultId: result.id!,
                }
                const _identifiers = { ...identifiers, ...commonIdentifiers }

                // Check if answer type is file upload
                if (resultDetailData.answer?.answerType === FILE_UPLOAD) {
                  const answerFile = resultDetailData.answer.answerFiles.find(answerFile => answerFile.id === identifiers?.fileId)

                  // Check if user want to upload
                  if (attrs?.isUpload && onUploadDocument)
                    onUploadDocument(_identifiers, {
                      answerCode: _answerCode,
                      document: _data,
                      transactionFileId: answerFile?.userFileTransactionId!,
                    })

                  // Check if user want to delete
                  if (attrs?.isDelete && onDeleteDocument)
                    onDeleteDocument({
                      ...identifiers,
                      ...commonIdentifiers,
                      userFileTransactionId: answerFile?.userFileTransactionId!,
                    })

                  // Check if user want to download
                  if (attrs?.isDownload && onDownloadDocument)
                    onDownloadDocument({ ...identifiers, ...commonIdentifiers, value: answerFile?.userFileBlobId! })

                  // Check if user want to add item
                  if (attrs?.isAddFileItem && onAddFileItem) onAddFileItem({ ...identifiers, ...commonIdentifiers })

                  // Check if user want to delete item
                  if (attrs?.isDeleteFileItem && onDeleteFileItem)
                    onDeleteFileItem({
                      ...identifiers,
                      ...commonIdentifiers,
                      userFileTransactionId: answerFile?.userFileTransactionId!,
                    })
                } else {
                  onUpdateUserAnswer && onUpdateUserAnswer(_identifiers, { answerCode: _answerCode, value: _data })
                }
              },
            )
          }
        } else {
          return ''
        }
      },
      [
        questionSetId,
        onAddFileItem,
        onDeleteDocument,
        onDeleteFileItem,
        onDownloadDocument,
        onUpdateUserAnswer,
        onUploadDocument,
        question_renderAnswer,
        question_renderQuestion,
      ],
    )

    /**
     * @description Render row class name
     *
     * @param {ISharedQuestionSetResult} record
     *
     * @return {string} string
     */
    const renderRowClassName = useCallback(
      (record: ISharedQuestionSetResult): string => {
        if (!isReadOnly) {
          const answers: ISharedAnswer[] = []

          record.resultDetail.forEach(resultDetail => {
            if (resultDetail.answer !== null && ![FILE_UPLOAD].includes(resultDetail.answer.answerType!)) {
              answers.push(resultDetail.answer)
            }
          })

          // Check if for general && relevant proof feature
          if (
            [ASSESSMENT_FEATURES.GENERAL_PROOF, ASSESSMENT_FEATURES.RELEVANT_PROOF].includes(featureName) &&
            !flattenDeep(answers.map(answer => answer.answerOption.map(answerOption => answerOption.isSelected))).some(
              isSelected => isSelected,
            )
          ) {
            return 'row-required'
          }

          // Check if user using multiple choice / checkbox / slider
          // And the value is empty / user not selecting any options, yet.
          // Check if feature other than general proof & relevant proof
          if (![ASSESSMENT_FEATURES.GENERAL_PROOF, ASSESSMENT_FEATURES.RELEVANT_PROOF].includes(featureName!)) {
            if (
              answers
                .filter(answer => [MULTIPLE_CHOICE, CHECKBOX, SLIDER].includes(answer.answerType!))
                .some(answer => !answer.answerOption.some(answerOption => answerOption.isSelected)) ||
              answers.filter(answer => [PARAGRAPH].includes(answer.answerType!)).some(answer => !answer.answerValue)
            ) {
              return 'row-required'
            }
          }
        }

        return ''
      },
      [featureName, isReadOnly],
    )

    /**
     * @description Render width of the every column
     *
     * @param {number} headerLength
     * @param {number} columnIndex
     *
     * @return {string} string
     */
    const renderColumnWidth = useCallback(
      (headerLength: number, columnIndex: number): string => {
        // Check if featureName is equal SELF_ASSESSMENT
        if (featureName === ASSESSMENT_FEATURES.SELF_ASSESSMENT) {
          if (columnIndex === 0) {
            return '40%'
          } else if (columnIndex === 2) {
            return '30%'
          } else {
            return `${30 / headerLength - 2}%`
          }
        }

        // Check if column not have children
        return `${100 / headerLength}%`
      },
      [featureName],
    )

    /**
     * @description Map columns
     *
     * @param {ISharedQuestionSetHeader[]} headers
     * @param {boolean} isChildren
     *
     * @return {ColumnsType<ISharedQuestionSetResult>} array of columns
     */
    const mapColumns = useCallback(
      (headers: ISharedQuestionSetHeader[], isChildren?: boolean): ColumnsType<ISharedQuestionSetResult> => {
        return headers.map((header, index) => {
          return {
            key: header.id!,
            dataIndex: header.id!,
            title: header.title,
            align: !isEmpty(header.children) || isChildren ? 'center' : undefined,
            width: renderColumnWidth(!isChildren ? headers.length : header.children.length, index),
            children: !isEmpty(header.children) ? mapColumns(header.children, true) : [],
            render: (_, record) => render(record, header.id!, isChildren),
          }
        })
      },
      [render, renderColumnWidth],
    )

    return (
      <div className="mb-4">
        <BaseTableBody
          dataSource={[...data.result]}
          columns={mapColumns(data.header)}
          rowClassName={renderRowClassName}
          className="mb-4"
          scroll={{ x: 1366 }}
          isBgPrimaryHeader
        />
      </div>
    )
  },
)

export { SharedQuestionSetTable }
